import type { ReactNode } from "react";

export type ChipTone = "success" | "attention" | "danger" | "ai" | "automation" | "operational" | "brand";

const toneClasses: Record<ChipTone, string> = {
  success: "bg-success-foreground/0 bg-success/15 text-success",
  attention: "bg-attention/20 text-attention",
  danger: "bg-danger/15 text-danger",
  ai: "bg-ai/15 text-ai",
  automation: "bg-automation/15 text-automation",
  operational: "bg-brand-soft-slate text-brand-slate",
  brand: "bg-brand-pale-blue text-brand-blue"
};

export function Chip({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-action text-[11px] font-bold uppercase tracking-wide whitespace-nowrap ${toneClasses[tone]}`}
    >
      {children}
    </span>
  );
}

/** Mono "instrument mark" — the Direction C graft used for verified numeric/status readouts. */
export function Mark({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  const dotClass: Record<ChipTone, string> = {
    success: "bg-success",
    attention: "bg-attention",
    danger: "bg-danger",
    ai: "bg-ai",
    automation: "bg-automation",
    operational: "bg-brand-slate",
    brand: "bg-primary"
  };
  const textClass: Record<ChipTone, string> = {
    success: "text-success",
    attention: "text-attention",
    danger: "text-danger",
    ai: "text-ai",
    automation: "text-automation",
    operational: "text-brand-slate",
    brand: "text-primary"
  };
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-[10.5px] font-semibold tracking-wide ${textClass[tone]}`}>
      <span className={`h-2 w-2 rounded-sm ${dotClass[tone]}`} aria-hidden="true" />
      {children}
    </span>
  );
}
